import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Terminal, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="login-page">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(99,102,241,0.08)_0%,rgba(5,5,5,0)_50%)]" />
      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="inline-flex items-center text-sm text-white/40 hover:text-white/70 mb-8 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to home
        </Link>
        <Card className="bg-white/[0.03] border-white/10 backdrop-blur-xl" data-testid="login-card">
          <CardHeader className="text-center pb-2">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Terminal className="w-7 h-7 text-indigo-400" />
              <span className="text-xl font-bold text-white" style={{fontFamily: 'Space Grotesk, sans-serif'}}>BuilderShift.ai</span>
            </div>
            <CardTitle className="text-2xl text-white">Welcome back</CardTitle>
            <CardDescription className="text-white/40">Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-white/60 text-sm">Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5"
                  placeholder="you@example.com"
                  required
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <Label className="text-white/60 text-sm">Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-black/50 border-white/10 focus:border-indigo-500/50 text-white mt-1.5"
                  placeholder="Enter password"
                  required
                  data-testid="login-password-input"
                />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-500 text-white h-10 shadow-[0_0_20px_rgba(99,102,241,0.3)]" disabled={loading} data-testid="login-submit-btn">
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
            <p className="text-center text-sm text-white/30 mt-6">
              No account?{' '}
              <Link to="/register" className="text-indigo-400 hover:text-indigo-300" data-testid="login-register-link">Create one</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
